import api from './api';

export interface RentalUnitTemplate {
  _id: string;
  templateName: string;
  companyId: string | null;
  category: 'Residential' | 'Commercial' | 'Mixed';
  unitType: string;
  title: string;
  description?: string;
  specifications: {
    bedrooms: number;
    bathrooms: number;
    balconies: number;
    area: {
      carpet: { value: number; unit: 'sqft' | 'sqm' };
      builtUp?: { value: number; unit: 'sqft' | 'sqm' };
      superBuiltUp?: { value: number; unit: 'sqft' | 'sqm' };
    };
    facing?: string;
    furnishingStatus: 'Unfurnished' | 'Semi-Furnished' | 'Fully-Furnished';
  };
  amenities: string[];
  pricingSuggestions: {
    rentRange: { min: number; max: number };
    securityDepositMonths: number;
    maintenanceCharges: {
      amount: number;
      included: boolean;
    };
  };
  defaultRules?: Array<{
    category: string;
    rule: string;
    mandatory: boolean;
  }>;
  tags: string[];
  usageCount: number;
  isActive: boolean;
  isGlobal?: boolean;
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  companyDetails?: {
    _id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface TemplateFilters {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  unitType?: string;
  companyId?: string;
  includeGlobal?: boolean;
  sortBy?: 'usageCount' | 'createdAt' | 'templateName';
  sortOrder?: 'asc' | 'desc';
}

export interface CreateTemplateFromUnitData {
  templateName: string;
  makeGlobal?: boolean;
}

class RentalUnitTemplateService {
  // Get templates with filters
  async getTemplates(filters: TemplateFilters = {}) {
    const response = await api.get('/rental-unit-templates', { params: filters });
    return response.data;
  }

  // Get popular templates
  async getPopularTemplates(limit: number = 10) {
    const response = await api.get('/rental-unit-templates/popular', { 
      params: { limit } 
    });
    return response.data;
  }

  // Get single template
  async getTemplate(id: string) {
    const response = await api.get(`/rental-unit-templates/${id}`);
    return response.data;
  }

  // Create new template
  async createTemplate(templateData: Partial<RentalUnitTemplate>) {
    const response = await api.post('/rental-unit-templates', templateData);
    return response.data;
  }

  // Create template from existing rental unit
  async createTemplateFromUnit(unitId: string, data: CreateTemplateFromUnitData) {
    const response = await api.post(`/rental-unit-templates/from-unit/${unitId}`, data);
    return response.data;
  }

  // Update template
  async updateTemplate(id: string, templateData: Partial<RentalUnitTemplate>) {
    const response = await api.put(`/rental-unit-templates/${id}`, templateData);
    return response.data;
  }

  // Delete template
  async deleteTemplate(id: string) {
    const response = await api.delete(`/rental-unit-templates/${id}`);
    return response.data;
  }

  // Record template usage
  async useTemplate(id: string) {
    const response = await api.post(`/rental-unit-templates/${id}/use`);
    return response.data;
  }

  // Search templates by keyword
  async searchTemplates(searchTerm: string, filters: TemplateFilters = {}) {
    const response = await api.get('/rental-unit-templates', {
      params: {
        search: searchTerm,
        ...filters
      }
    });
    return response.data;
  }

  // Get templates for a specific company (including global ones)
  async getCompanyTemplates(companyId: string, includeGlobal: boolean = true) {
    const response = await api.get('/rental-unit-templates', {
      params: {
        companyId,
        includeGlobal
      }
    });
    return response.data;
  }
}

export default new RentalUnitTemplateService();